import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PostCallListItem } from './usePostCall';

const POLL_INTERVAL = 15 * 1000; // 15 seconds
const MAX_POLL_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_POLL_ATTEMPTS = Math.floor(MAX_POLL_DURATION / POLL_INTERVAL); // 20 attempts

/**
 * Hook to poll for postcall metadata after a call ends.
 * Polls every 15 seconds until a postcall is found or 5 minutes have elapsed.
 */
export const usePostCallPolling = (callerId?: number) => {
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pollAttemptsRef = useRef<number>(0);

  const startPolling = () => {
    // Clear any existing polling
    stopPolling();

    // Don't start if no callerId
    if (!callerId) {
      return;
    }

    // Reset tracking
    startTimeRef.current = Date.now();
    pollAttemptsRef.current = 0;

    // Initial refetch
    queryClient.refetchQueries(['postCallList', callerId, true], { exact: true });

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - (startTimeRef.current || 0);
      pollAttemptsRef.current += 1;

      // Stop if max duration or max attempts reached
      if (elapsed >= MAX_POLL_DURATION || pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        stopPolling();
        return;
      }

      // Refetch postcall list and check if we found incomplete postcalls
      queryClient.refetchQueries(['postCallList', callerId, true], { exact: true }).then(() => {
        // Check if we have incomplete postcalls now
        const postCallList = queryClient.getQueryData<PostCallListItem[]>(['postCallList', callerId, true]);
        if (postCallList && postCallList.length > 0) {
          // Found a postcall, stop polling
          stopPolling();
        }
      });
    }, POLL_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    startTimeRef.current = null;
    pollAttemptsRef.current = 0;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return { startPolling, stopPolling };
};
